import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import ReactDOM from "react-dom";
import { observer } from "mobx-react";
import { signalsData } from "Stores/signalsData";
import { agentsData } from "Stores/agentsData";
import { settingsStoreData } from "Stores/settingsData";
import userDetail from "Stores/userDetail";
import SignalIcon from "./SignalIcon";
import SignalPicker from "./SignalPicker";
import SellerProfileLine from "./SellerProfileLine";
import { categoryClass, costText, costUnitOf, entityNoun, countText, scopeCostLine, usesSellerProfile, creditsPerUnitOf, creditMath } from "./signalMeta";
import { Info } from "./Info";

const ANY = "__any";
const DEFAULT_CAP = 200;
const ALL = "__all";
const OFTEN_LABEL = { 1: "Daily", 7: "Weekly", 30: "Monthly", 90: "Quarterly" };
/** "every 30 days" → "every month": how a check interval reads in a sentence. */
const everyText = days => ({ 1: "every day", 7: "every week", 30: "every month", 90: "every 3 months" }[days] || `every ${days} days`);

/** Turns a saved signal's stored conditions back into the form's local state. */
const initialConditions = (definition, saved) => {
  const out = {};
  const stored = ((saved && saved.signal) || {}).conditions || {};
  (definition.conditions || []).forEach(c => {
    const v = stored[c.key];
    if (c.kind === "CHOICE") out[c.key] = Array.isArray(v) && v.length ? v[0] : ANY;
    else out[c.key] = { min: v && v.min != null ? String(v.min) : "", max: v && v.max != null ? String(v.max) : "" };
  });
  return out;
};

/** The conditions the API accepts: "any" and empty ranges are simply left out. */
const toConditions = (definition, values) => {
  const out = {};
  (definition.conditions || []).forEach(c => {
    const v = values[c.key];
    if (c.kind === "CHOICE") {
      if (v && v !== ANY) out[c.key] = [v];
    } else if (v && (v.min !== "" || v.max !== "")) {
      const range = {};
      if (v.min !== "") range.min = Number(v.min);
      if (v.max !== "") range.max = Number(v.max);
      out[c.key] = range;
    }
  });
  return out;
};

const Row = ({ label, hint, children }) => (
  <div className="sg-frm">
    <div className="sg-frm__l"><b>{label}</b>{hint && <small>{hint}</small>}</div>
    <div className="sg-frm__r">{children}</div>
  </div>
);

/**
 * The Track form: who to watch, how often, a monthly credit cap and where to hear about it, with an optional
 * agent hand-off. The side card spells out what saying yes costs. `saved` is the SavedSignalView when editing.
 */
const SignalSetupDrawer = ({ definition, saved, presetListId = null, onClose }) => {
  // A team without agents can't use the agent option, so don't offer it.
  const agentsOn = !!(userDetail?.userFeatureFlag || {}).agentsEnabled;
  const isAccount = definition.entityType === "ACCOUNT";
  const noun = entityNoun(definition.entityType);
  const free = costUnitOf(definition) === "free";
  // Champion moves watch the contacts on one list you pick (your past customers); "all" isn't allowed.
  const champion = definition.id === "champion_move";
  const existing = (saved && saved.signal) || null;
  // Paid signals start with nothing picked: "all my contacts" can cost thousands of credits on the first check.
  const [listId, setListId] = useState(() => {
    if (existing) return existing.scopeType === "LIST" && existing.scopeListId ? existing.scopeListId : ALL;
    return presetListId || (free ? ALL : null);
  });
  // The intervals this signal offers (headcount moves slowly, so no daily there); some also take every few days.
  const options = (definition.checkOptionDays && definition.checkOptionDays.length ? definition.checkOptionDays : [7, 30]).slice().sort((a, b) => a - b);
  const fewDays = !!definition.everyFewDays;
  const [checkDays, setCheckDays] = useState((existing && existing.checkEveryDays) || definition.defaultCheckDays || 30);
  const [customOn, setCustomOn] = useState(fewDays && !!(existing && existing.checkEveryDays) && !options.includes(existing.checkEveryDays));
  const [creditCap, setCreditCap] = useState(String((existing && existing.creditCap) || (saved && saved.creditCap) || DEFAULT_CAP));
  const [automate, setAutomate] = useState(false);
  const [conditions, setConditions] = useState(() => initialConditions(definition, saved));
  const [createTask, setCreateTask] = useState(!!(existing && existing.createTask));
  const [name, setName] = useState((existing && existing.name) || "");
  // Account signals read through a seller profile; null until the line hands back the team's default.
  const withProfile = usesSellerProfile(definition.id);
  const [sellerProfileId, setSellerProfileId] = useState((existing && existing.sellerProfileId) || (saved && saved.sellerProfileId) || null);
  const [cooldown, setCooldown] = useState(existing && existing.cooldownDays != null ? String(existing.cooldownDays) : "30");
  const [estimate, setEstimate] = useState(null);
  const [allEstimate, setAllEstimate] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    const onKey = e => { if (e.key === "Escape") onClose(false); };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose]);

  useEffect(() => {
    if (isAccount) agentsData.fetchAccountLists();
    else agentsData.fetchContactLists();
  }, [isAccount]);

  const scope = { signalType: definition.id, scopeType: listId && listId !== ALL ? "LIST" : "ALL", scopeListId: listId && listId !== ALL ? listId : null };
  // What "all" would cost, so the option can say so before anyone picks it.
  useEffect(() => {
    if (free) return undefined;
    let live = true;
    signalsData.estimate({ signalType: definition.id, scopeType: "ALL", scopeListId: null, conditions: {}, checkDays: 30 }).then(r => live && setAllEstimate(r));
    return () => { live = false; };
  }, [free, definition.id]);
  useEffect(() => {
    if (free || listId == null) return undefined;
    let live = true;
    setEstimate(null);
    // An existing signal names itself, so its own records don't count as "already watched".
    signalsData.estimate({ ...scope, conditions: toConditions(definition, conditions), checkDays, ...(existing && existing.id ? { savedSignalId: existing.id } : {}) }).then(r => live && setEstimate(r || {}));
    return () => { live = false; };
  }, [listId, checkDays, free]); // eslint-disable-line

  const lists = (isAccount ? agentsData.accountLists : agentsData.contactLists) || [];
  const plural = isAccount ? "companies" : "contacts";
  const allRecords = allEstimate && allEstimate.records != null ? allEstimate.records : null;
  const listOptions = [
    ...(champion ? [] : [{ value: ALL, label: isAccount ? "All my accounts" : "All my contacts", hint: allRecords != null ? countText(allRecords, allEstimate.capped) : "" }]),
    ...lists.map(l => ({ value: l.id, label: l.name, hint: l.count != null ? `${Number(l.count).toLocaleString()}` : "" }))
  ];
  const picked = lists.find(l => l.id === listId);
  const est = estimate || {};
  const count = v => countText(v, est.capped);
  const records = est.records != null ? est.records : picked && picked.count != null ? picked.count : null;
  const checks = est.checksPerMonth || (checkDays >= 30 ? 30 / checkDays : Math.round(30 / checkDays));
  // Billed records (already watched ones are not charged twice) × credits per record; the cap warning uses the same.
  const perUnit = creditsPerUnitOf(definition);
  const billed = est.newRecords != null ? est.newRecords : records;
  const { perCheck, perMonth } = creditMath(billed, perUnit, checks);
  const cap = Number(creditCap) || 0;
  const n = v => (v == null ? "–" : Number(v).toLocaleString());
  const needsScope = !free && listId == null;

  // The cost panel (receipt + one note). Calm by default; amber when the cap will pause it, red when credits run out.
  const watchName = listId == null ? `the ${isAccount ? "companies" : "people"} you pick` : listId !== ALL ? (picked ? picked.name : "your list") : `all your ${isAccount ? "accounts" : "contacts"}`;
  const balance = settingsStoreData.totalRemainingCredits || 0;
  const counting = !free && !needsScope && estimate == null;
  const priced = !free && !needsScope && !counting && perCheck != null;
  const approx = customOn ? "about " : "";
  const checksOn = budget => (perCheck > 0 ? Math.floor(budget / perCheck) : 0);
  const tone = !priced ? "calm" : perMonth > balance ? "red" : cap > 0 && perMonth > cap ? "amber" : "calm";
  const afterChecks = k => (k <= 0 ? "partway through the first check" : `after ${k === 1 ? "the first check" : `${k} checks`}`);
  const note = free ? "Free: it watches your own activity as it happens."
    : needsScope ? `Pick who to watch to see the cost. ${costText(definition)}.`
    : !billed && est.alreadyWatched > 0 ? `All ${n(est.alreadyWatched)} are already checked by another of your signals, so this adds no credits.`
    : tone === "red" ? `More than your ${n(balance)} credits: checks stop ${afterChecks(checksOn(balance))}. Try a slower schedule or a smaller list.`
    : tone === "amber" ? `Pauses at your ${n(cap)} cap ${afterChecks(checksOn(cap))}. Raise the cap or watch a smaller list.`
    : `Runs now, then ${everyText(checkDays)} · you have ${n(balance)} credits`;
  const breakdown = free ? "" : needsScope || counting ? costText(definition)
    : `${count(billed)} ${plural} × ${perUnit} credit${perUnit === 1 ? "" : "s"} each check, ${checks} check${checks === 1 ? "" : "s"} a month. Every move it finds is included. Checks pause at your monthly cap.`;

  const save = async () => {
    if (needsScope) {
      setError("Pick who to watch.");
      return;
    }
    if (!free && cap <= 0) {
      setError("Set a monthly credit cap above 0.");
      return;
    }
    setBusy(true);
    setError("");
    const payload = {
      ...scope,
      name: name.trim(),
      conditions: toConditions(definition, conditions),
      createTask,
      automate: automate || !!(existing && existing.wantsAgent),
      cooldownDays: Number(cooldown),
      ...(free ? {} : { checkEveryDays: Number(checkDays), creditCap: cap }),
      ...(withProfile ? { sellerProfileId } : {})
    };
    const result = await signalsData.saveSignal(existing ? existing.id : null, payload, { records });
    setBusy(false);
    if (result.error) setError(result.error);
    else onClose(true, automate ? result.signal : null);
  };


  // Portalled to the body so the backdrop covers the right rail too (.mainContent is a containing block).
  return ReactDOM.createPortal(
    <div className="sg-drawer-bg" onClick={() => onClose(false)}>
      <div className="sg-drawer sg-drawer--wide" onClick={e => e.stopPropagation()}>
        <div className="sg-drawer__head">
          <span className={`sg-tile sg-tile--lg ${categoryClass(definition.category)}`}><SignalIcon id={definition.id} size={22} /></span>
          <div className="sg-drawer__title">
            <h2>{existing ? `Edit: ${definition.label}` : `Track ${definition.label.toLowerCase()}`}</h2>
            <p>{definition.description}</p>
          </div>
          <button className="sg-x" onClick={() => onClose(false)} aria-label="Close">×</button>
        </div>

        <div className="sg-drawer__body sg-track">
          <div className="sg-track__form">
            {withProfile && <SellerProfileLine value={sellerProfileId} onChange={setSellerProfileId} />}
            {definition.available === false && (
              <div className="sg-warn">Nothing detects this signal yet. You can set it up now, but it won't find anything until it ships.</div>
            )}
            <Row label="Who to watch" hint={champion ? "Your past customers list" : `All your ${isAccount ? "accounts" : "contacts"}, or one list`}>
              <div className="sg-scope">
                <SignalPicker value={listId} options={listOptions} onChange={setListId} placeholder="Pick who to watch" searchable className="sg-pick--wide"
                  emptyText={`You have no lists yet. Create a ${isAccount ? "company" : "contact"} list, then come back to pick it.`} />
                {champion && (
                  <small className="sg-hint">Pick the list that holds your past customers (any contact list, including one synced from your CRM). You hear when someone on it moves to a new company.</small>
                )}
                {!free && listId != null && (
                  <small className="sg-scope__cost">
                    {estimate == null ? <span className="sg-loadbar sg-loadbar--wide" role="status" aria-label="Working out the cost" /> : scopeCostLine({ ...est, records }, plural, count, perUnit)}
                  </small>
                )}
              </div>
            </Row>
            {!free && (
              <Row label="How often" hint={options.includes(1) ? "More often hears sooner, at more credits" : "Headcount moves slowly; monthly is usually enough"}>
                <div className="sg-chips">
                  {options.map(days => (
                    <button key={days} type="button" className={`sg-chip ${!customOn && checkDays === days ? "on" : ""}`} onClick={() => { setCustomOn(false); setCheckDays(days); }}>
                      {OFTEN_LABEL[days] || `Every ${days} days`}
                    </button>
                  ))}
                  {fewDays && (customOn ? (
                    <label className="sg-chip on cs-every">
                      Every <input type="number" min="1" max="30" value={checkDays} onChange={e => setCheckDays(Math.max(1, Math.min(30, Number(e.target.value) || 1)))} aria-label="Check every how many days" /> days
                    </label>
                  ) : (
                    <button type="button" className="sg-chip" onClick={() => { setCustomOn(true); setCheckDays(3); }}>Custom</button>
                  ))}
                </div>
              </Row>
            )}
            {!free && (
              <Row label="Monthly credit cap" hint="Checks pause when it is reached">
                <div className="sg-cap">
                  <input className="sg-input" type="number" min="1" value={creditCap} onChange={e => setCreditCap(e.target.value)} aria-label="Monthly credit cap" />
                  <span>credits a month</span>
                </div>
              </Row>
            )}

            <details className="sg-adv">
              <summary>More options</summary>
              {(definition.conditions || []).map(c => (
                <Row key={c.key} label={c.label} hint="Optional">
                  {c.kind === "CHOICE" ? (
                    <SignalPicker value={conditions[c.key]} onChange={v => setConditions({ ...conditions, [c.key]: v })}
                      options={[{ value: ANY, label: "Any" }, ...(c.options || []).map(o => ({ value: o, label: o }))]} />
                  ) : (
                    <div className="sg-range">
                      <input className="sg-input" type="number" min="0" placeholder="At least" value={conditions[c.key].min}
                        onChange={e => setConditions({ ...conditions, [c.key]: { ...conditions[c.key], min: e.target.value } })} />
                      <input className="sg-input" type="number" min="0" placeholder="At most" value={conditions[c.key].max}
                        onChange={e => setConditions({ ...conditions, [c.key]: { ...conditions[c.key], max: e.target.value } })} />
                    </div>
                  )}
                </Row>
              ))}
              {!isAccount && (
                <Row label="Create a task" hint="A follow-up for the owner, due the next day">
                  <button type="button" className={`sg-switch ${createTask ? "on" : ""}`} aria-label="Create a task" aria-pressed={createTask} onClick={() => setCreateTask(!createTask)} />
                </Row>
              )}
              <Row label="Name" hint="Named from the signal and list unless you type one">
                <input className="sg-input" value={name} maxLength={120} placeholder={`${definition.label} · ${picked ? picked.name : listOptions.length ? listOptions[0].label : "your list"}`} onChange={e => setName(e.target.value)} />
              </Row>
              <Row label={`Quiet days per ${noun}`} hint="No repeat alert for the same record">
                <input className="sg-input sg-input--short" type="number" min="0" max="365" value={cooldown} onChange={e => setCooldown(e.target.value)} />
              </Row>
            </details>

            {/* Already handled by an agent: say which, instead of offering to set one up again. */}
            {saved && saved.agents && saved.agents.length > 0 ? (
              <div className="sg-agentblock is-on">
                <div className="sg-agentblock__head">
                  <SignalIcon id="robot" size={18} />
                  <div className="sg-grow">
                    <b>{saved.agents[0].name || "An agent"} handles each one</b>
                    <small>Change what it does from the agent's page.</small>
                  </div>
                  <Link className="sg-link" to={`/agents/${saved.agents[0].id}`}>Open agent</Link>
                </div>
              </div>
            ) : !agentsOn ? null : (
            <div className={`sg-agentblock ${automate ? "is-on" : ""}`}>
              <div className="sg-agentblock__head">
                <SignalIcon id="robot" size={18} />
                <div className="sg-grow">
                  <b>Let an agent handle each one (optional)</b>
                  <small>Same thing every time? An agent can do it for you.</small>
                </div>
                <button type="button" className={`sg-switch ${automate ? "on" : ""}`} aria-label="Let an agent handle each one" aria-pressed={automate} onClick={() => setAutomate(!automate)} />
              </div>
              <ol className="sg-agentblock__steps">
                <li>Next you see its steps, already filled in for this signal.</li>
                <li>Change or add any, then turn it on.</li>
                <li>Anything it sends asks you first.</li>
              </ol>
            </div>
            )}
            {error && <div className="sg-error">{error}</div>}
          </div>

          <aside className="sg-okcard">
            <div className="sg-okcard__lead">Needs your OK</div>
            <b>What tracking does</b>
            <ol>
              <li title={watchName}><span className="sg-okcard__who">Watches {watchName}</span></li>
              <li>Each move lands in your feed</li>
              <li>You act on it, or an agent does</li>
            </ol>
            {/* The receipt: what one check and a month cost, then one note on when it runs or what will stop it. */}
            {free ? (
              <div className="sg-receipt"><div className="sg-receipt__row"><span>Your own activity</span><b>Free</b></div></div>
            ) : !needsScope && (
              <div className="sg-receipt">
                {checks !== 1 && <div className="sg-receipt__row"><span>Each check</span><b>{counting ? <span className="sg-loadbar" role="status" aria-label="Working out the cost" /> : `${countText(perCheck, est.capped)} credits`}</b></div>}
                <div className="sg-receipt__row"><span>Per month</span><b className={`is-${tone}`}>{counting ? <span className="sg-loadbar" role="status" aria-label="Working out the cost" /> : `${approx}${countText(perMonth, est.capped)} credits`}</b></div>
                {est.alreadyWatched > 0 && billed > 0 && <div className="sg-receipt__row"><span>Watched by another signal</span><b>{n(est.alreadyWatched)} free</b></div>}
              </div>
            )}
            <div className={`sg-notebar is-${tone}`}>
              {counting ? <span className="sg-loadbar sg-loadbar--line" aria-hidden="true" /> : <span>{note}</span>}
              {!free && !counting && <Info text={breakdown} />}
            </div>
          </aside>
        </div>

        <div className="sg-drawer__foot">
          <button className="sg-btn sg-btn--ghost" onClick={() => onClose(false)}>Cancel</button>
          {needsScope && <span className="sg-foot-hint">Pick who to watch first</span>}
          {/* When the panel stacks under the form (narrow screens), the price still sits by the button. */}
          {priced && <span className={`sg-foot-cost is-${tone}`}>{approx}{countText(perMonth, est.capped)} credits a month</span>}
          <button className="sg-btn" disabled={busy || needsScope} onClick={save}>
            {busy ? "Saving…" : existing ? "Save changes" : "Start tracking"}
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default observer(SignalSetupDrawer);
